import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, Tabs, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { FEE_REQUEST_META, FeeRequest, FeeRequestStatus } from '../../../types/finance';
import { PAYMENT_METHOD_LABEL } from '../../../utils/paymentMethods';
import { useAuth } from '../../../context/AuthContext';
import { useApprovalRule } from '../../../hooks/useApprovalRule';
import { useTimezone } from '../../../hooks/useTimezone';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';
import EmptyState from '../../common/EmptyState';
import ReasonDialog from '../../common/ReasonDialog';
import TableRowSkeletons from '../../common/TableRowSkeletons';
import { ToneChip } from '../../common/StatusChip';
import { payerNumber } from '../../../utils/personRef';

const TABS: Array<{ value: FeeRequestStatus; label: string }> = [
  { value: 'PENDING', label: 'Waiting for approval' },
  { value: 'POSTED', label: 'Approved' },
  { value: 'REJECTED', label: 'Sent back' },
];

/** Waivers, write-offs, refunds and deposit returns — decided by someone other than their maker. */
const FeeRequestsPanel: React.FC<{ onDecided?: () => void }> = ({ onDecided }) => {
  const theme = useTheme();
  const { decodedToken } = useAuth();
  const { ownBlocked } = useApprovalRule();
  const { formatDate } = useTimezone();
  const { canWrite } = useModulePermissions();
  const money = useCurrencyFormat();
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<FeeRequestStatus>('PENDING');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState<{ items: FeeRequest[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<FeeRequest | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await financeService.listFeeRequests({ status, page: page + 1, limit });
      setData({ items: r.items, total: r.pagination.totalItems });
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load requests.');
    }
  }, [status, page, limit]);

  useEffect(() => { setData(null); load(); }, [load]);

  const approve = async (r: FeeRequest) => {
    setBusyId(r._id);
    try {
      await financeService.approveFeeRequest(r._id);
      enqueueSnackbar(`${FEE_REQUEST_META[r.kind].label} approved and posted`, { variant: 'success' });
      onDecided?.();
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not approve this.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const items = data?.items ?? [];
  return (
    <Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={status} onChange={(_, v) => { setStatus(v); setPage(0); }} sx={{ px: 1, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', minHeight: 44 } }}>
          {TABS.map(t => <Tab key={t.value} value={t.value} label={t.label} />)}
        </Tabs>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                <TableCell>Request</TableCell>
                <TableCell>Family</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>{status === 'PENDING' ? 'Raised by' : 'Decided by'}</TableCell>
                {status === 'PENDING' && <TableCell align="right" sx={{ width: 220 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {!data ? <TableRowSkeletons columns={status === 'PENDING' ? 6 : 5} rows={4} /> : items.map(r => {
                const meta = FEE_REQUEST_META[r.kind];
                const own = ownBlocked(r.createdBy === decodedToken?.userId);
                const mayDecide = canWrite(meta.permission);
                return (
                  <TableRow key={r._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{meta.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Challan <Box component="span" sx={{ fontFamily: 'monospace' }}>{r.invoice?.invoiceNumber}</Box>
                        {r.invoice?.period?.label && ` · ${r.invoice.period.label}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.person?.name ?? '—'}</Typography>
                      {r.person && payerNumber(r.person) && <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>{payerNumber(r.person)}</Typography>}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" noWrap title={r.reason}>{r.reason}</Typography>
                      {r.rejectionReason && <Typography variant="caption" color="warning.dark" display="block">Sent back: {r.rejectionReason}</Typography>}
                      {r.undone && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap title={r.undone.reason ?? undefined}>
                          Undone{r.undone.byName && ` by ${r.undone.byName}`}{r.undone.at && ` on ${formatDate(r.undone.at)}`}{r.undone.reason && `: ${r.undone.reason}`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} color={r.undone ? 'text.disabled' : undefined} sx={{ fontVariantNumeric: 'tabular-nums', textDecoration: r.undone ? 'line-through' : undefined }}>{money(r.amount)}</Typography>
                      {r.method && <Typography variant="caption" color="text.secondary">{PAYMENT_METHOD_LABEL[r.method]}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{status === 'PENDING' ? r.createdByName : r.decidedByName}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(status === 'PENDING' ? r.createdAt : r.decidedAt ?? r.createdAt)}</Typography>
                    </TableCell>
                    {status === 'PENDING' && (
                      <TableCell align="right">
                        {mayDecide ? (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" color="inherit" onClick={() => setRejecting(r)} disabled={busyId === r._id}>Send back</Button>
                            <Tooltip title={own ? 'You raised this — someone else must approve it.' : ''}>
                              <span>
                                <Button size="small" variant="contained" disableElevation onClick={() => approve(r)} disabled={own || busyId === r._id}>
                                  {busyId === r._id ? 'Posting…' : 'Approve'}
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        ) : <ToneChip tone="pending" size="small" label="Waiting" />}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {data && items.length === 0 && (
          <Box sx={{ p: 3 }}>
            <EmptyState icon={<CheckCircleOutline />} title={status === 'PENDING' ? 'Nothing waiting' : 'Nothing here yet'}
              description="Large waivers, write-offs, refunds and deposit returns raised on challans appear here." dense />
          </Box>
        )}
        <TablePagination component="div" count={data?.total ?? 0} page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit} onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} />
      </Paper>

      <ReasonDialog
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title={rejecting ? `Send back this ${FEE_REQUEST_META[rejecting.kind].label.toLowerCase()}?` : ''}
        consequence="Nothing is posted. Whoever raised it sees your reason."
        facts={rejecting ? [{ label: 'Challan', value: rejecting.invoice?.invoiceNumber ?? '' }, { label: 'Amount', value: money(rejecting.amount) }] : []}
        confirmText="Send back"
        confirmColor="warning"
        reasonPlaceholder="What needs to change?"
        onConfirm={async reason => {
          await financeService.rejectFeeRequest(rejecting!._id, reason);
          enqueueSnackbar('Sent back', { variant: 'success' });
          await load();
        }}
      />
    </Box>
  );
};

export default FeeRequestsPanel;
